import type { AdminFeature } from '@lib/features/types'
import {
  emptyPermissionFormValues,
  type FeaturePermissionRow,
  type PermissionFormValues
} from '@lib/features/types'
import {
  Button,
  Group,
  Modal,
  Stack,
  Switch,
  Text,
  TextInput,
  Textarea
} from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { useEffect, useState } from 'react'

type PermissionFormModalProps = {
  feature: Pick<AdminFeature, 'id' | 'key' | 'name'>
  permission?: {
    id: string
    name: string
    description: string | null
    disabled: boolean
  } | null
  opened: boolean
  onClose: () => void
  onSubmit: (values: PermissionFormValues) => Promise<void>
  saving?: boolean
}

export function PermissionFormModal({
  feature,
  permission,
  opened,
  onClose,
  onSubmit,
  saving = false
}: PermissionFormModalProps) {
  const isEdit = Boolean(permission)
  const [values, setValues] = useState<PermissionFormValues>(
    emptyPermissionFormValues
  )

  useEffect(() => {
    if (!opened) return
    if (permission) {
      setValues({
        name: permission.name,
        description: permission.description || '',
        disabled: permission.disabled
      })
    } else {
      setValues(emptyPermissionFormValues)
    }
  }, [opened, permission])

  const handleSubmit = async () => {
    if (!isEdit && !values.name.trim()) return
    await onSubmit(values)
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title={
        isEdit
          ? `Edit permission — ${feature.name}`
          : `Add permission — ${feature.name}`
      }
      size="md"
    >
      <Stack gap="md">
        {isEdit ? (
          <TextInput
            label="Permission"
            value={values.name}
            disabled
            description="Identifier is immutable after creation"
            styles={{ input: { fontFamily: 'monospace' } }}
          />
        ) : (
          <TextInput
            label="Permission name"
            description={`Use ${feature.key}.action or hub.${feature.key}.action for hub-native modules`}
            value={values.name}
            onChange={(e) =>
              setValues((v) => ({ ...v, name: e.target.value }))
            }
            required
            styles={{ input: { fontFamily: 'monospace' } }}
          />
        )}
        <Textarea
          label="Description"
          value={values.description}
          onChange={(e) =>
            setValues((v) => ({ ...v, description: e.target.value }))
          }
          minRows={2}
        />
        <Switch
          label="Globally disabled"
          description="Deprecated platform-wide; hidden from role editors and JWT"
          checked={values.disabled}
          onChange={(e) =>
            setValues((v) => ({
              ...v,
              disabled: e.currentTarget.checked
            }))
          }
        />
        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={saving}
            onClick={() => void handleSubmit()}
            disabled={!isEdit && !values.name.trim()}
          >
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  )
}

export function permissionFromRow(
  row: FeaturePermissionRow
): NonNullable<PermissionFormModalProps['permission']> | null {
  if (!row.permission_id || !row.permission_name) return null
  return {
    id: row.permission_id,
    name: row.permission_name,
    description: row.permission_description,
    disabled: Boolean(row.permission_disabled)
  }
}
